import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useStore } from "../../../stores/store";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSubmitData } from "../../../hooks/useSubmitData";
import { useSetMenu } from "../../../hooks/useSetMenu";
import FormLayout from "../../Generic/FormLayout";
import * as yup from "yup";
import FormSelect from "../../Generic/FormSelect";
import './ToolFeature.css';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ClearAllIcon from '@mui/icons-material/ClearAll';

// Modern component for rendering a group of checkboxes for a given criterion
const CriterionCheckboxGroup = ({ criterion, selectedIds, toggleCheckbox }) => {
  const selectedCount = criterion.values.filter(v => selectedIds.includes(v.id)).length;

  return (
    <div className="criterion-group-card">
      <div className="criterion-group-header">
        <div className="criterion-group-name">
          <CategoryIcon className="icon" fontSize="small" />
          {criterion.name}
        </div>
        {selectedCount > 0 && (
          <span className="criterion-group-badge">{selectedCount} selected</span>
        )}
      </div>
      <div className="criterion-group-body">
        {criterion.values.map((val) => {
          const isSelected = selectedIds.includes(val.id);
          return (
            <div
              key={val.id}
              className={`criterion-value-chip ${isSelected ? 'selected' : ''}`}
              onClick={() => toggleCheckbox(val.id)}
            >
              <span className="checkbox-icon">
                {isSelected ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
              </span>
              {val.value}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Add = () => {
  const { mainStore } = useStore();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  useSetMenu(mainStore.ToolFeatureMenuType, mainStore.ToolFeatureMenuNo);

  // Yup schema for required cascading selects
  const schema = yup.object({
    panelId: yup.string().required(t("selectRequired")),
    categoryId: yup.string().required(t("selectRequired")),
    subcategoryId: yup.string().required(t("selectRequired")),
    toolPassportId: yup.string().required(t("selectRequired")),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  // We'll use our custom submit hook to call our BulkCreate endpoint
  const sendData = useSubmitData(axiosPrivate, "/ToolFeatures/BulkCreate", "/ToolFeature/List");
  const onSubmit = (data) => {
    // Build the request with parsed integers and selected criteriaValueIds array
    const requestBody = {
      panelId: parseInt(data.panelId, 10),
      categoryId: parseInt(data.categoryId, 10),
      subcategoryId: parseInt(data.subcategoryId, 10),
      toolPassportId: parseInt(data.toolPassportId, 10),
      criteriaValueIds: selectedCriteriaValueIds, // our array of checked criteria values
    };

    if (requestBody.criteriaValueIds.length === 0) {
      enqueueSnackbar(t("pleaseSelectAtLeastOneCriteriaValue"), { variant: "warning" });
      return;
    }

    sendData(requestBody);
  };
  const onCancel = () => navigate(-1);

  // Cascading select state
  const [panels, setPanels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [toolPassports, setToolPassports] = useState([]);

  const [selectedPanel, setSelectedPanel] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedToolPassport, setSelectedToolPassport] = useState(null);

  // State for criteria with values loaded based on subcategory
  const [criterias, setCriterias] = useState([]);
  // For bulk selection we maintain an array of selected criteriaValue IDs
  const [selectedCriteriaValueIds, setSelectedCriteriaValueIds] = useState([]);

  // Load initial panel options on mount
  useEffect(() => {
    const loadPanels = async () => {
      try {
        const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
        setPanels(panelsResponse.data);
      } catch (err) {
        enqueueSnackbar(t("loadDataError"), { variant: "error" });
      }
    };
    loadPanels();
  }, [axiosPrivate, enqueueSnackbar, t]);

  // Handler: when panel changes, load categories
  const handlePanelChange = async (selectedOption) => {
    setValue("panelId", selectedOption ? selectedOption.value : "", { shouldValidate: true });
    setSelectedPanel(selectedOption);
    // Clear dependent selects
    setCategories([]);
    setSelectedCategory(null);
    setSubcategories([]);
    setSelectedSubcategory(null);
    setToolPassports([]);
    setSelectedToolPassport(null);
    setCriterias([]);
    setSelectedCriteriaValueIds([]);

    if (!selectedOption) return;
    try {
      const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
      setCategories(
        typeof response.data === "string" && response.data === "No records" ? [] : response.data
      );
    } catch (error) {
      enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
    }
  };

  // Handler: when category changes, load subcategories
  const handleCategoryChange = async (selectedOption) => {
    setValue("categoryId", selectedOption ? selectedOption.value : "", { shouldValidate: true });
    setSelectedCategory(selectedOption);
    setSubcategories([]);
    setSelectedSubcategory(null);
    setToolPassports([]);
    setSelectedToolPassport(null);
    setCriterias([]);
    setSelectedCriteriaValueIds([]);

    if (!selectedOption) return;
    try {
      const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
      setSubcategories(
        typeof response.data === "string" && response.data === "No records" ? [] : response.data
      );
    } catch (error) {
      enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
    }
  };

  // Handler: when subcategory changes, load tool passports and load criteria with values (for checkboxes)
  const handleSubcategoryChange = async (selectedOption) => {
    setValue("subcategoryId", selectedOption ? selectedOption.value : "", { shouldValidate: true });
    setSelectedSubcategory(selectedOption);
    setToolPassports([]);
    setSelectedToolPassport(null);
    setCriterias([]);
    setSelectedCriteriaValueIds([]);

    if (!selectedOption) return;
    try {
      // Load all potential tool passports for this subcategory
      const response1 = await axiosPrivate.get(`/ToolPassports/SelectOptionsBySubcategory/${selectedOption.value}`);
      const allToolPassports = typeof response1.data === "string" && response1.data === "No records" 
        ? [] 
        : response1.data;
      
      // Get existing tool passports that already have criteria assigned
      const existingResponse = await axiosPrivate.get(`/ToolFeatures/Filtered?subcategoryId=${selectedOption.value}`);
      const existingFeatures = Array.isArray(existingResponse.data) ? existingResponse.data : [];
      
      // Create a set of tool passport IDs that already have criteria
      const existingToolPassportIds = new Set();
      existingFeatures.forEach(feature => {
        existingToolPassportIds.add(feature.toolPassportId.toString());
      });
      
      // Filter out tool passports that already have criteria
      const availableToolPassports = allToolPassports.filter(
        tp => !existingToolPassportIds.has(tp.value)
      );
      
      setToolPassports(availableToolPassports);
    } catch (error) {
      enqueueSnackbar(t("errorLoadingToolPassports"), { variant: "error" });
    }
    try {
      // Load criteria with values for this subcategory
      const response2 = await axiosPrivate.get(`/Criterias/WithValuesBySubcategory/${selectedOption.value}`);
      const data = response2.data;
      // If data is a string with "No records", set it as an empty array.
      // Also, if it's not an array, wrap it in an array.
      if (typeof data === "string" && data === "No records") {
        setCriterias([]);
      } else if (!Array.isArray(data)) {
        setCriterias([data]);
      } else {
        setCriterias(data);
      }
    } catch (error) {
      enqueueSnackbar(t("errorLoadingCriteria"), { variant: "error" });
    }
  };

  // Handler: when tool passport changes
  const handleToolPassportChange = (selectedOption) => {
    setValue("toolPassportId", selectedOption ? selectedOption.value : "", { shouldValidate: true });
    setSelectedToolPassport(selectedOption);
  };

  // Toggle a checkbox for a given criteria value id
  const toggleCriteriaValue = (cvId) => {
    setSelectedCriteriaValueIds((prev) => {
      if (prev.includes(cvId)) {
        return prev.filter((id) => id !== cvId);
      } else {
        return [...prev, cvId];
      }
    });
  };

  return (
    <FormLayout title={t("toolFeatureCreateTitle")} onSubmit={handleSubmit(onSubmit)} onCancel={onCancel}>
      {/* Cascading selects */}
      <FormSelect
        label="Panel"
        name="panelId"
        value={selectedPanel}
        register={register}
        errors={errors}
        colSize={3}
        options={panels}
        onChange={handlePanelChange}
        isClearable={true}
      />
      <FormSelect
        label="Category"
        name="categoryId"
        value={selectedCategory}
        register={register}
        errors={errors}
        colSize={3}
        options={categories}
        onChange={handleCategoryChange}
        isClearable={true}
      />
      <FormSelect
        label="Subcategory"
        name="subcategoryId"
        value={selectedSubcategory}
        register={register}
        errors={errors}
        colSize={3}
        options={subcategories}
        onChange={handleSubcategoryChange}
        isClearable={true}
      />
      <FormSelect
        label="Tool Passport"
        name="toolPassportId"
        value={selectedToolPassport}
        register={register}
        errors={errors}
        colSize={3}
        options={toolPassports}
        onChange={handleToolPassportChange}
        isClearable={true}
      />

      {/* Now, display checkboxes for each criterion's values */}
      {Array.isArray(criterias) && criterias.length > 0 && (
        <div className="criteria-selection-container">
          <div className="criteria-selection-header">
            <PlaylistAddCheckIcon />
            <span>{t("selectCriteriaValues")}</span>
          </div>
          <div className="criteria-selection-body">
            {/* Selection Summary */}
            {selectedCriteriaValueIds.length > 0 && (
              <div className="selection-summary">
                <div className="selection-summary-text">
                  <CheckCircleIcon />
                  <span>{t("valuesSelected")}</span>
                  <span className="selection-summary-count">{selectedCriteriaValueIds.length}</span>
                </div>
                <button
                  type="button"
                  className="selection-summary-clear"
                  onClick={() => setSelectedCriteriaValueIds([])}
                >
                  <ClearAllIcon fontSize="small" style={{ marginRight: 4 }} />
                  {t("clearAll")}
                </button>
              </div>
            )}
            {criterias.map((crit) => (
              <CriterionCheckboxGroup
                key={crit.id}
                criterion={crit}
                selectedIds={selectedCriteriaValueIds}
                toggleCheckbox={toggleCriteriaValue}
              />
            ))}
          </div>
        </div>
      )}
    </FormLayout>
  );
};

export default Add;